import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Trash2, PlusCircle, GripVertical, ChevronDown, GitBranch } from 'lucide-react';

type QuestionType = 'text' | 'rating' | 'multiple_choice' | 'checkboxes' | 'textarea';

interface QuestionOption {
  id: string;
  value: string;
}

// Branching rules for answer-based question flow
interface BranchingAction {
  action: 'goto_question' | 'skip_to_end' | 'show_investigation';
  targetQuestionId?: string;
}

interface BranchingRules {
  answers: {
    [answerText: string]: BranchingAction;
  };
}

interface Question {
  id: string;
  type: QuestionType;
  text: string;
  required: boolean;
  options?: QuestionOption[];
  placeholder?: string;
  visible_on_high_rating?: boolean;
  visible_on_low_rating?: boolean;
  branching_rules?: BranchingRules;
}

export type { Question, QuestionOption, BranchingRules, BranchingAction };

interface ReviewFormBuilderSimpleProps {
  questions: Question[];
  onAddQuestion: (type: QuestionType) => void;
  onUpdateQuestion: (id: string, field: keyof Question, value: unknown) => void;
  onRemoveQuestion: (id: string) => void;
  onAddOption: (questionId: string) => void;
  onUpdateOption: (questionId: string, optionId: string, value: string) => void;
  onRemoveOption: (questionId: string, optionId: string) => void;
  onReorderQuestions?: (questions: Question[]) => void;
}

const ReviewFormBuilderSimple: React.FC<ReviewFormBuilderSimpleProps> = ({
  questions,
  onAddQuestion,
  onUpdateQuestion,
  onRemoveQuestion,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  onReorderQuestions,
}) => {
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [draggedOver, setDraggedOver] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, questionId: string) => {
    setDraggedItem(questionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, questionId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOver(questionId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedOver(null);
  };

  const handleDrop = (e: React.DragEvent, targetQuestionId: string) => {
    e.preventDefault();
    
    if (!draggedItem || !onReorderQuestions || draggedItem === targetQuestionId) {
      setDraggedItem(null);
      setDraggedOver(null);
      return;
    }

    const draggedIndex = questions.findIndex(q => q.id === draggedItem);
    const targetIndex = questions.findIndex(q => q.id === targetQuestionId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      setDraggedOver(null);
      return;
    }

    const newQuestions = [...questions];
    const [draggedQuestion] = newQuestions.splice(draggedIndex, 1);
    newQuestions.splice(targetIndex, 0, draggedQuestion);

    onReorderQuestions(newQuestions);
    setDraggedItem(null);
    setDraggedOver(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedOver(null);
  };
  
  const renderQuestionEditor = (question: Question, index: number) => {
    const isDragging = draggedItem === question.id;
    const isDraggedOver = draggedOver === question.id;
    
    return (
      <div 
        key={question.id} 
        className={`relative p-4 border rounded-lg transition-all duration-200 ${
          isDragging 
            ? 'opacity-50 scale-95 bg-muted/40' 
            : isDraggedOver 
              ? 'bg-muted/60 border-primary/50 shadow-md' 
              : 'bg-muted/20'
        }`}
        draggable={!!onReorderQuestions}
        onDragStart={(e) => handleDragStart(e, question.id)}
        onDragOver={(e) => handleDragOver(e, question.id)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, question.id)}
        onDragEnd={handleDragEnd}
      >
          <div className="space-y-4">
            {/* Question Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {onReorderQuestions && (
                  <div 
                    className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1"
                    title="Drag to reorder"
                  >
                    <GripVertical className="h-4 w-4" />
                  </div>
                )}
                <Label className="text-sm font-semibold text-muted-foreground">
                  Question {index + 1} ({question.type.replace('_', ' ')})
                </Label>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveQuestion(question.id)}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {/* Question Text */}
            <div className="space-y-2">
              <Input
                value={question.text}
                onChange={(e) => onUpdateQuestion(question.id, 'text', e.target.value)}
                placeholder={`Enter your ${question.type.replace('_', ' ')} question...`}
                className="text-base"
              />
            </div>

            {/* Question Options for Multiple Choice and Checkboxes */}
            {(question.type === 'multiple_choice' || question.type === 'checkboxes') && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Options:</Label>
                <div className="space-y-2">
                  {question.options?.map((option) => (
                    <div key={option.id} className="flex items-center gap-2">
                      <Input
                        value={option.value}
                        onChange={(e) => onUpdateOption(question.id, option.id, e.target.value)}
                        placeholder="Option text"
                        className="flex-1 text-sm"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveOption(question.id, option.id)}
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        disabled={question.options && question.options.length <= 1}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onAddOption(question.id)}
                    className="w-full text-xs"
                  >
                    <PlusCircle className="h-3 w-3 mr-1" />
                    Add Option
                  </Button>
                </div>

                {/* Branching Rules Configuration */}
                {questions.length > 1 && (
                  <Collapsible className="mt-3">
                    <CollapsibleTrigger className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                      <GitBranch className="h-3 w-3" />
                      <span>Configure branching rules</span>
                      <ChevronDown className="h-3 w-3" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 space-y-2 pl-5 border-l-2 border-muted">
                      <p className="text-xs text-muted-foreground mb-2">
                        Define what happens when each option is selected:
                      </p>
                      {question.options?.map((option) => {
                        const currentRule = question.branching_rules?.answers?.[option.value];
                        return (
                          <div key={`branch-${option.id}`} className="flex items-center gap-2 text-xs">
                            <span className="truncate max-w-[120px] text-muted-foreground" title={option.value}>
                              "{option.value}"
                            </span>
                            <span className="text-muted-foreground">→</span>
                            <Select
                              value={currentRule?.action || 'continue'}
                              onValueChange={(value) => {
                                const newRules: BranchingRules = {
                                  answers: {
                                    ...(question.branching_rules?.answers || {}),
                                    [option.value]: value === 'continue'
                                      ? undefined as any // Will be filtered out
                                      : { action: value as BranchingAction['action'] }
                                  }
                                };
                                // Filter out 'continue' (undefined) entries
                                const filteredAnswers = Object.fromEntries(
                                  Object.entries(newRules.answers).filter(([_, v]) => v !== undefined)
                                );
                                onUpdateQuestion(
                                  question.id,
                                  'branching_rules',
                                  Object.keys(filteredAnswers).length > 0 ? { answers: filteredAnswers } : undefined
                                );
                              }}
                            >
                              <SelectTrigger className="h-7 w-[140px] text-xs">
                                <SelectValue placeholder="Continue" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="continue">Continue next</SelectItem>
                                <SelectItem value="skip_to_end">Skip to end</SelectItem>
                                <SelectItem value="goto_question">Go to question...</SelectItem>
                              </SelectContent>
                            </Select>
                            {currentRule?.action === 'goto_question' && (
                              <Select
                                value={currentRule.targetQuestionId || ''}
                                onValueChange={(targetId) => {
                                  const newRules: BranchingRules = {
                                    answers: {
                                      ...(question.branching_rules?.answers || {}),
                                      [option.value]: {
                                        action: 'goto_question',
                                        targetQuestionId: targetId
                                      }
                                    }
                                  };
                                  onUpdateQuestion(question.id, 'branching_rules', newRules);
                                }}
                              >
                                <SelectTrigger className="h-7 w-[120px] text-xs">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {questions
                                    .filter(q => q.id !== question.id)
                                    .map((q, qIndex) => (
                                      <SelectItem key={q.id} value={q.id}>
                                        Q{questions.findIndex(x => x.id === q.id) + 1}: {q.text.slice(0, 20)}...
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            )}

            {/* Required Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`required-${question.id}`}
                checked={question.required}
                onCheckedChange={(checked) => onUpdateQuestion(question.id, 'required', checked)}
              />
              <Label htmlFor={`required-${question.id}`} className="text-sm text-muted-foreground">
                Required field
              </Label>
            </div>

            {/* Visibility Settings */}
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-xs font-semibold text-muted-foreground">Show this question for:</Label>
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`high-rating-${question.id}`}
                    checked={question.visible_on_high_rating !== false}
                    onCheckedChange={(checked) => onUpdateQuestion(question.id, 'visible_on_high_rating', checked)}
                  />
                  <Label htmlFor={`high-rating-${question.id}`} className="text-xs text-muted-foreground">
                    High ratings
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`low-rating-${question.id}`}
                    checked={question.visible_on_low_rating !== false}
                    onCheckedChange={(checked) => onUpdateQuestion(question.id, 'visible_on_low_rating', checked)}
                  />
                  <Label htmlFor={`low-rating-${question.id}`} className="text-xs text-muted-foreground">
                    Low ratings
                  </Label>
                </div>
              </div>
            </div>
          </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
        {/* Add Question Buttons */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Add Questions:</Label>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddQuestion('text')}
              className="text-xs"
            >
              <PlusCircle className="h-3 w-3 mr-1" />
              Text Field
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddQuestion('textarea')}
              className="text-xs"
            >
              <PlusCircle className="h-3 w-3 mr-1" />
              Long Text
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddQuestion('rating')}
              className="text-xs"
            >
              <PlusCircle className="h-3 w-3 mr-1" />
              Rating
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddQuestion('multiple_choice')}
              className="text-xs"
            >
              <PlusCircle className="h-3 w-3 mr-1" />
              Multiple Choice
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddQuestion('checkboxes')}
              className="text-xs"
            >
              <PlusCircle className="h-3 w-3 mr-1" />
              Checkboxes
            </Button>
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-4">
          {questions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No questions added yet.</p>
              <p className="text-xs mt-1">Use the buttons above to add your first question.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Questions ({questions.length}):</Label>
              <div className="space-y-3">
                {questions.map((question, index) => renderQuestionEditor(question, index))}
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

export default ReviewFormBuilderSimple;